import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router";

import { ApiError } from "./api/client.ts";
import { Login } from "./features/auth/Login.tsx";
import { RequireSession } from "./features/auth/RequireSession.tsx";
import { Learning } from "./features/analysis/Learning.tsx";
import { Metrics } from "./features/analysis/Metrics.tsx";
import { AppShell } from "./features/shell/AppShell.tsx";
import { Dashboard } from "./features/trades/Dashboard.tsx";
import { NewTrade } from "./features/trades/NewTrade.tsx";
import { TradeDetail } from "./features/trades/TradeDetail.tsx";
import { Timeline } from "./features/trades/Timeline.tsx";

import { registerServiceWorker } from "./sw-register.ts";
import { lazyPart } from "./ui/lazyPart.tsx";
import { applyTheme, readTheme } from "./ui/theme.ts";
import { UpdateToast } from "./ui/UpdateToast.tsx";

import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/login.css";
import "./styles/timeline.css";
import "./styles/calendar.css";
import "./styles/instruments.css";
import "./styles/images.css";
import "./styles/library.css";
import "./styles/phrases.css";
import "./styles/notifications.css";
import "./styles/update.css";
import "./styles/session.css";
import "./styles/owner.css";

/*
 * Lo que no se abre al entrar viaja aparte (ver `lazyPart`): la biblioteca, la
 * semana y la configuracion entera. El panel, la bitacora y el detalle siguen
 * en el trozo principal, porque son lo primero que se pide.
 */
const Library = lazyPart(() => import("./features/library/Library.tsx").then((m) => m.Library));
const Phrases = lazyPart(() => import("./features/library/Phrases.tsx").then((m) => m.Phrases));
const WeekReview = lazyPart(() =>
  import("./features/journal/WeekReview.tsx").then((m) => m.WeekReview),
);
const Catalogs = lazyPart(() => import("./features/settings/Catalogs.tsx").then((m) => m.Catalogs));
const SuggestionsAdmin = lazyPart(() =>
  import("./features/settings/SuggestionsAdmin.tsx").then((m) => m.SuggestionsAdmin),
);
const InstrumentPage = lazyPart(() =>
  import("./features/settings/InstrumentPage.tsx").then((m) => m.InstrumentPage),
);
const Instruments = lazyPart(() =>
  import("./features/settings/Instruments.tsx").then((m) => m.Instruments),
);
const Settings = lazyPart(() => import("./features/settings/Settings.tsx").then((m) => m.Settings));
const Users = lazyPart(() => import("./features/settings/Users.tsx").then((m) => m.Users));

/**
 * Router, QueryClient y registro del service worker (SPEC 15.2).
 *
 * TanStack Query se encarga de la cache, la invalidacion y los reintentos en red
 * irregular (SPEC 3.2), que es exactamente el caso de un telefono. Pero NO
 * reintenta un 4xx: un 400 de validacion o un 409 de concurrencia no mejoran
 * porque se repitan, y reintentarlos solo retrasa el mensaje al usuario.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (attempt, error) => {
        if (error instanceof ApiError && error.status < 500) return false;

        return attempt < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: { retry: false },
  },
});

/* El tema, ANTES de montar: si se aplicara dentro de React, la primera pintura
   saldria en claro y el usuario veria un fogonazo blanco. */
applyTheme(readTheme());

/* Solo hace algo en produccion (SPEC 15.7). En desarrollo, Vite sirve los
   modulos sin hash y un service worker cacheando encima vuelve loco el ciclo. */
registerServiceWorker();

const root = document.getElementById("root");

if (root === null) {
  throw new Error("Falta el elemento #root en index.html.");
}

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<RequireSession />}>
            <Route element={<AppShell />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/trades" element={<Timeline />} />
              <Route path="/trades/new" element={<NewTrade />} />
              <Route path="/trades/:id" element={<TradeDetail />} />
              <Route path="/semana" element={<WeekReview />} />
              <Route path="/metricas" element={<Metrics />} />
              <Route path="/aprendizaje" element={<Learning />} />
              <Route path="/biblioteca" element={<Library />} />
              <Route path="/frases" element={<Phrases />} />
              <Route path="/instrumentos" element={<Instruments />} />
              {/* La ruta estatica primero y la del simbolo despues: asi se lee
                  que /instrumentos sigue siendo el listado y no un simbolo que
                  se llama asi. */}
              <Route path="/instrumentos/:id" element={<InstrumentPage />} />
              <Route path="/catalogos/sugerencias" element={<SuggestionsAdmin />} />
              <Route path="/catalogos/:lista" element={<Catalogs />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/usuarios" element={<Users />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
      <UpdateToast />
    </QueryClientProvider>
  </StrictMode>,
);
